'use client'

import posthog from 'posthog-js'
import { useEffect } from 'react'

// Catches React render errors in the root layout itself — the last-resort
// boundary the per-route error.tsx files cannot reach. Fully self-contained:
// it replaces the root layout, so it owns <html> + <body> and must not import
// feature components, providers, or next/link (routing may be unavailable on
// a root crash). Inline styles because Tailwind's stylesheet is injected by
// the crashed layout's <head>.
export default function GlobalError({
    error,
    reset,
}: {
    error: Error & { digest?: string }
    reset: () => void
}) {
    useEffect(() => {
        posthog.captureException(error)
        console.error('Root layout error:', error)
    }, [error])

    return (
        <html lang="en">
            <body
                style={{
                    margin: 0,
                    fontFamily: 'Georgia, "Times New Roman", Times, serif',
                    background: '#faf8f4',
                    color: '#1a1a18',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        minHeight: '100dvh',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '4rem 1.25rem',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '2rem',
                            textAlign: 'center',
                            maxWidth: '28rem',
                            width: '100%',
                        }}
                    >
                        <p
                            style={{
                                margin: 0,
                                fontSize: '0.6875rem',
                                letterSpacing: '0.18em',
                                textTransform: 'uppercase',
                                color: '#6b6b67',
                                fontFamily: 'system-ui, sans-serif',
                            }}
                        >
                            Application error
                        </p>
                        <h1
                            style={{
                                margin: 0,
                                fontSize: '1.75rem',
                                fontWeight: 400,
                                letterSpacing: '-0.02em',
                                lineHeight: 1.2,
                                color: '#1a1a18',
                            }}
                        >
                            Something went wrong.
                        </h1>
                        <p
                            style={{
                                margin: 0,
                                fontSize: '1rem',
                                lineHeight: 1.65,
                                color: '#3d3d39',
                                fontStyle: 'italic',
                                maxWidth: '32ch',
                            }}
                        >
                            An unexpected error interrupted the page. Try again,
                            or return to the start.
                        </p>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                fontSize: '0.8125rem',
                                color: '#6b6b67',
                                fontStyle: 'italic',
                                width: '100%',
                                margin: '1rem 0',
                            }}
                        >
                            <span
                                style={{
                                    flex: 1,
                                    height: '1px',
                                    background: '#d8d4cc',
                                }}
                                aria-hidden
                            />
                            <button
                                type="button"
                                onClick={reset}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 0,
                                    fontSize: 'inherit',
                                    fontFamily: 'inherit',
                                    fontStyle: 'inherit',
                                    color: '#3d3d39',
                                }}
                            >
                                try again →
                            </button>
                            <span
                                style={{
                                    flex: 1,
                                    height: '1px',
                                    background: '#d8d4cc',
                                }}
                                aria-hidden
                            />
                            {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- global-error renders outside the App Router; next/link needs router context that is unavailable on a root-layout crash */}
                            <a
                                href="/"
                                style={{
                                    color: '#3d3d39',
                                    textDecoration: 'none',
                                    fontSize: 'inherit',
                                    fontFamily: 'inherit',
                                    fontStyle: 'inherit',
                                }}
                            >
                                return →
                            </a>
                            <span
                                style={{
                                    flex: 1,
                                    height: '1px',
                                    background: '#d8d4cc',
                                }}
                                aria-hidden
                            />
                        </div>
                    </div>
                </div>
            </body>
        </html>
    )
}
